import { useState, useRef } from 'react';
import Button from 'components/CustomButtons/Button.jsx';
import imagePlaceholder from 'assets/img/image_placeholder.jpg';
import { toast } from 'react-toastify';
import { formatFileSize } from 'helpers/helpers';
import { useTranslation } from 'react-i18next';
import makeStyles from '@material-ui/styles/makeStyles';

const useStyles = makeStyles({
  root: {
    display: 'inline-block',
    marginBottom: '9px',
    textAlign: 'center',
  },
  input: {
    display: 'none',
  },
  imgWrapper: {
    position: 'relative',
    display: 'inline-block',
    marginBottom: '10px',
    overflow: 'hidden',
    textAlign: 'center',
    verticalAlign: 'middle',
    maxWidth: '250px',
    boxShadow:
      '0 10px 30px -12px rgba(0, 0, 0, 0.42), 0 4px 25px 0px rgba(0, 0, 0, 0.12), 0 8px 10px -5px rgba(0, 0, 0, 0.2)',
  },
  image: {
    maxHeight: '100%',
    width: '100%',
    height: 'auto',
    marginRight: 'auto',
    marginLeft: 'auto',
    display: 'block',
    maxWidth: '100%',
  },
  buttonsWrapper: {
    display: 'block',
    justifyContent: 'inherit',
  },
  error: {
    maxWidth: '250px',
    color: 'red',
  },
});

const ImageUploadWithProportions = ({
  stateName,
  getImage,
  removeImage,
  previewUrl,
  buttonText,
  imgId,
  addButtonProps,
  changeButtonProps,
  removeButtonProps,
  defaultImage,
  maxFileSize,
  isDisabled,
  proportionsConstraint,
}) => {
  const [propHeight, propWidth] = proportionsConstraint ?? [];
  const classes = useStyles();
  const { t } = useTranslation();
  const [isProportionsValid, setIsProportionsValid] = useState(true);
  const fileInputRef = useRef(null);

  const handleImageChange = e => {
    e.preventDefault();
    if (e.target.files.length) {
      const selectedFile = e.target.files[0];

      if (maxFileSize && selectedFile.size > maxFileSize) {
        toast.error(
          t('$*form.imageUpload.sizeError', {
            maxFileSize: formatFileSize(maxFileSize),
          })
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = event => {
        const img = new window.Image();
        img.onload = () => {
          const desiredRatio = propWidth / propHeight;
          const imgRatio = img.naturalWidth / img.naturalHeight;
          const cropWidth = img.naturalWidth;
          let cropHeight = img.naturalHeight;
          const sx = 0;
          let sy = 0;

          if (imgRatio < desiredRatio) {
            cropHeight = img.naturalWidth / desiredRatio;
            sy = (img.naturalHeight - cropHeight) / 2;
            setIsProportionsValid(false);
          } else {
            setIsProportionsValid(true);
          }

          const canvas = document.createElement('canvas');
          canvas.width = cropWidth;
          canvas.height = cropHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(
            img,
            sx,
            sy,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight
          );

          canvas.toBlob(blob => {
            const croppedFile = new File([blob], selectedFile.name, {
              type: selectedFile.type,
            });
            getImage(stateName, croppedFile);
          }, selectedFile.type);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const onInputClick = event => {
    event.target.value = '';
  };

  const handleRemove = () => {
    setIsProportionsValid(true);
    removeImage(stateName);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  return (
    <div className={classes.root}>
      <input
        type="file"
        onClick={onInputClick}
        onChange={handleImageChange}
        ref={fileInputRef}
        accept=".jpg,.jpeg,.png,.gif,.tiff,.webp"
        className={classes.input}
      />
      <div className={classes.imgWrapper}>
        <img
          id={imgId}
          src={previewUrl ?? defaultImage ?? imagePlaceholder}
          alt="..."
          className={classes.image}
        />
      </div>
      {!isProportionsValid && (
        <p className={classes.error}>
          {t('form.imageUpload.proportionsError', {
            propHeight,
            propWidth,
          })}
        </p>
      )}
      <div className={classes.buttonsWrapper}>
        {previewUrl ? (
          <span>
            <Button
              {...changeButtonProps}
              onClick={handleClick}
              disabled={isDisabled}
            >
              {t('form.change')}
            </Button>
            <Button
              {...removeButtonProps}
              onClick={handleRemove}
              disabled={isDisabled}
            >
              <i className="fas fa-times" /> {t('form.delete')}
            </Button>
          </span>
        ) : (
          <span>
            <Button
              {...addButtonProps}
              onClick={handleClick}
              disabled={isDisabled}
            >
              {buttonText || t('form.selectPhoto')}
            </Button>
            {previewUrl && (
              <Button
                {...removeButtonProps}
                onClick={handleRemove}
                disabled={isDisabled}
              >
                <i className="fas fa-times" /> {t('form.delete')}
              </Button>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default ImageUploadWithProportions;
